/**
 * Questo file definisce il componente AdminRoute.
 * - È un componente di ordine superiore (HOC) utilizzato per proteggere le rotte dell'applicazione
 * che devono essere accessibili solo agli utenti con il ruolo di 'admin'.
 * - Controlla il ruolo dell'utente attualmente autenticato nello stato di Redux.
 * - Se l'utente è un amministratore, renderizza i componenti figli (la pagina protetta).
 * - Se l'utente non è un amministratore, lo reindirizza a una pagina sicura, come la dashboard.
 */

// Import delle librerie necessarie da React e React-Router.
import React from 'react';
// Import del hook useSelector per accedere allo stato globale di Redux.
import { useSelector } from 'react-redux';
// Import del componente Navigate per effettuare reindirizzamenti programmatici.
import { Navigate } from 'react-router-dom';

/**
 * Componente funzionale AdminRoute.
 * - Accetta come prop 'children', che rappresenta il componente o la pagina da renderizzare
 * se il controllo di autorizzazione ha successo.
 *
 * @param {object} props - Le props del componente.
 * @param {React.ReactNode} props.children - Il componente React da renderizzare se l'utente è un admin.
 * @returns {React.ReactElement} - Ritorna il componente figlio se l'utente è un admin,
 * altrimenti il componente Navigate per il reindirizzamento.
 */
const AdminRoute = ({ children }) => {
  // Estrae l'oggetto 'user' dallo slice 'auth' dello store di Redux.
  // L'oggetto 'user' contiene le informazioni dell'utente loggato, incluso il suo ruolo.
  const { user } = useSelector((state) => state.auth);

  // Esegue il controllo di autorizzazione.
  // Verifica se l'oggetto 'user' esiste e se la sua proprietà 'ruolo' è uguale a 'admin'.
  // L'uso di 'user?.ruolo' (optional chaining) previene errori se 'user' fosse null o undefined.
  if (user?.ruolo === 'admin') {
    // Se l'utente è un amministratore, la condizione è vera.
    // Viene quindi restituito 'children', permettendo il rendering della rotta protetta.
    return children;
  }

  // Se la condizione non è soddisfatta (l'utente non è admin o non è loggato),
  // viene reindirizzato alla pagina '/dashboard'.
  // L'attributo 'replace' fa sì che la pagina corrente venga sostituita nella cronologia di navigazione,
  // impedendo all'utente di tornare indietro alla rotta admin con il pulsante "indietro" del browser.
  return <Navigate to="/dashboard" replace />;
};

// Esporta il componente AdminRoute per poterlo utilizzare nel sistema di routing dell'app.
export default AdminRoute;